import {
  invalidateEverythingTimesheetsRelated,
  useInvalidateGetApprovalEntries,
  useInvalidateGetApprovalTimesheets,
  useInvalidateGetTimesheetRows,
  useInvalidateTimesheet,
} from '@sit/client-shared';
import { NotificationPayload, NotificationTopicSchema } from '@sit/notifications-shared';
import { useQueryClient } from '@tanstack/react-query';
import { useTimesheetSaveSettled } from '@web/api/timesheet/use-post-timesheet-save-settled';
import { useTimesheetSubmitSettled } from '@web/api/timesheet/use-post-timesheet-submit-settled';
import { PropsWithChildren, useCallback } from 'react';
import { useOnLatestNotification } from './hooks/useLatestNotification';

function useHandleGlobalNotifications() {
  const queryClient = useQueryClient();
  const onSaveSettled = useTimesheetSaveSettled();
  const onSubmitSettled = useTimesheetSubmitSettled();
  const invalidateTimesheetsQuery = useInvalidateGetApprovalTimesheets();
  const invalidateEntriesQuery = useInvalidateGetApprovalEntries();
  const invalidateTimesheetRowsQuery = useInvalidateGetTimesheetRows();
  const invalidateTimesheetQuery = useInvalidateTimesheet();
  const handleApprovals = useCallback(
    (notification: NotificationPayload) => {
      void invalidateTimesheetsQuery();
      // Some approval notifications omit affected timesheet IDs. Invalidate cached
      // details too, so reopening a timesheet cannot reuse its pre-approval status.
      void invalidateEverythingTimesheetsRelated(queryClient);
      void invalidateEntriesQuery();
      const invalidateTs = (timesheetId: string) => {
        void invalidateTimesheetQuery(timesheetId);
        void invalidateTimesheetRowsQuery(timesheetId);
      };

      const { data } = notification;
      if (!data) return;

      if ('timesheets' in data && Array.isArray(data.timesheets)) {
        for (const timesheetId of data.timesheets) {
          invalidateTs(timesheetId);
        }
      } else if ('timesheetMapping' in data) {
        const { timesheetMapping } = data as {
          timesheetMapping: Record<string, { timesheetId: string }>;
        };
        const timesheetIds = [...new Set(Object.values(timesheetMapping).map((mapping) => mapping.timesheetId))];
        for (const timesheetId of timesheetIds) {
          invalidateTs(timesheetId);
        }
      }
    },
    [invalidateEntriesQuery, invalidateTimesheetQuery, invalidateTimesheetRowsQuery, invalidateTimesheetsQuery, queryClient],
  );

  useOnLatestNotification(NotificationTopicSchema.enum.APPROVALS, handleApprovals);
  const handleSave = useCallback(
    (notification: NotificationPayload) => {
      const { data } = notification;
      onSaveSettled(data?.timesheetId, notification.type === 'SUCCESS');
    },
    [onSaveSettled],
  );
  useOnLatestNotification(NotificationTopicSchema.enum.SAVE_TIMESHEET, handleSave);
  const handleSubmit = useCallback(
    (notification: NotificationPayload) => {
      const { data } = notification;
      onSubmitSettled(data?.timesheetId, notification.type === 'SUCCESS');
      if (data && 'timesheetId' in data) {
        void invalidateTimesheetQuery(data.timesheetId);
        if (notification.type === 'SUCCESS') {
          void invalidateEverythingTimesheetsRelated(queryClient);
        }
      }
    },
    [onSubmitSettled, invalidateTimesheetQuery, queryClient],
  );
  useOnLatestNotification(NotificationTopicSchema.enum.SUBMIT_TIMESHEET, handleSubmit);
}

export function NotificationGlobalHandler({ children }: PropsWithChildren<Record<string, unknown>>) {
  useHandleGlobalNotifications();
  return <>{children}</>;
}
